/**
 * Runtime API version matrix.
 *
 * The app fetches `api_version_matrix`, then builds a flat lookup:
 * `{ [api_name]: api_version_url }`.
 *
 * Release selection uses `VITE_BUILD_VERSION` from `vite.config.js` first. If
 * that release is not present for an API, the newest lower release is used.
 */

import { useStatsigClient } from '@statsig/react-bindings'
import { useEffect } from 'react'

const emptyMatrix = Object.create(null)

let matrixRows = []
let hydrateAttempted = false
let hydrateSucceeded = false
let versionBasedApiUsageEnabled = false

export const apiVersionMatrix = emptyMatrix
export const apiVersions = apiVersionMatrix
export const VERSION_BASED_API_USAGE_FEATURE_FLAG = 'fe-745_mandate_version_based_usage_of_api'

function clearObject(target) {
  Object.keys(target).forEach((key) => {
    delete target[key]
  })
}

export function getAppReleaseVersion() {
  return String(
    import.meta.env.VITE_BUILD_VERSION || import.meta.env.VITE_RELEASE_VERSION || '0.0.0',
  )
}

function semverTriple(releaseLabel) {
  const matched = /^(\d+)\.(\d+)\.(\d+)/.exec(String(releaseLabel ?? '').trim())
  if (!matched) return null

  return {
    major: Number(matched[1]),
    minor: Number(matched[2]),
    patch: Number(matched[3]),
  }
}

export function compareSemanticRelease(leftRelease, rightRelease) {
  const left = semverTriple(leftRelease)
  const right = semverTriple(rightRelease)

  if (!left || !right) return String(leftRelease ?? '').localeCompare(String(rightRelease ?? ''))

  if (left.major !== right.major) return left.major - right.major
  if (left.minor !== right.minor) return left.minor - right.minor

  return left.patch - right.patch
}

export function stripInvocationQueryAndHash(invokeSegment) {
  if (!invokeSegment || typeof invokeSegment !== 'string') return ''

  let base = invokeSegment.trim()
  const idxQ = base.indexOf('?')
  const idxHash = base.indexOf('#')
  let cut = base.length

  if (idxQ !== -1) cut = Math.min(cut, idxQ)
  if (idxHash !== -1) cut = Math.min(cut, idxHash)

  base = base.slice(0, cut)

  while (base.length > 0 && base.endsWith('/')) base = base.slice(0, -1)

  return base
}

export function pathLeafPreserveCase(invokeSegment) {
  const base = stripInvocationQueryAndHash(String(invokeSegment || '')).replace(/\\/g, '/')
  const slashIdx = Math.max(base.lastIndexOf('/'), base.lastIndexOf('\\'))

  return (slashIdx === -1 ? base : base.slice(slashIdx + 1)).trim()
}

export function canonicalInvokeBase(invokeSegment) {
  return pathLeafPreserveCase(invokeSegment).toLowerCase()
}

export function canonicalApiName(apiName) {
  return canonicalInvokeBase(apiName)
}

export function postgresRpcBaseFromMatrixUrl(invokeSegment) {
  const clean = stripInvocationQueryAndHash(String(invokeSegment || '').replace(/\\/g, '/'))

  if (/^rpc\//i.test(clean)) return pathLeafPreserveCase(clean)

  return clean.trim()
}

export function inferApiTypeFromMatrixUrl(apiVersionUrl) {
  const raw = stripInvocationQueryAndHash(String(apiVersionUrl || ''))
  if (!raw) return 'edgeFunctions'

  const normalized = raw.replace(/\\/g, '/')
  const lowerPath = normalized.toLowerCase()
  const hasSlash = normalized.split('/').length > 1

  if (/\brpc\b/.test(lowerPath) || /^rpc\//i.test(normalized)) return 'rpcFunctions'
  if (hasSlash) return 'edgeFunctions'

  return /^[a-z]\w*$/i.test(canonicalInvokeBase(raw)) ? 'rpcFunctions' : 'edgeFunctions'
}

export function parseVersionComparable(versionLabel) {
  const raw = String(versionLabel || '').trim()
  const lower = raw.toLowerCase()
  const vnMatch = /^v(\d+)$/i.exec(lower)

  if (vnMatch) {
    return {
      major: Number(vnMatch[1]),
      minor: 0,
      patch: 0,
      raw,
      kind: 'vn',
    }
  }

  const semverMatch = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?/.exec(lower)
  if (semverMatch) {
    return {
      major: Number(semverMatch[1]),
      minor: semverMatch[2] === undefined ? 0 : Number(semverMatch[2]),
      patch: semverMatch[3] === undefined ? 0 : Number(semverMatch[3]),
      raw,
      kind: 'semver',
    }
  }

  return {
    major: 0,
    minor: 0,
    patch: 0,
    raw,
    kind: 'other',
  }
}

export function compareVersionRank(leftLabel, rightLabel) {
  const left = parseVersionComparable(leftLabel)
  const right = parseVersionComparable(rightLabel)

  if (left.kind === 'other' && right.kind !== 'other') return -1
  if (right.kind === 'other' && left.kind !== 'other') return 1
  if (left.kind === 'other' && right.kind === 'other')
    return String(left.raw).localeCompare(String(right.raw))

  if (left.major !== right.major) return left.major - right.major
  if (left.minor !== right.minor) return left.minor - right.minor

  return left.patch - right.patch
}

export function compositeMatrixKey(apiType, baseCanonical) {
  return `${apiType}:${baseCanonical}`
}

function rowApiName(row) {
  return String(
    row?.api_name || row?.apiVersionName || canonicalInvokeBase(row?.api_version_url || ''),
  ).trim()
}

function rowApiVersionUrl(row) {
  return String(row?.api_version_url || '')
    .replace('/functions/v1/', '')
    .replace('/rest/v1/rpc/', '')
    .trim()
}

function isRowForRelease(row, releaseVersion) {
  const rowRelease = String(row?.release_version || '').trim()
  if (!rowRelease || !releaseVersion) return false

  return compareSemanticRelease(rowRelease, releaseVersion) <= 0
}

function isNewerMatrixRow(candidate, current) {
  if (!current) return true

  const releaseCompare = compareSemanticRelease(candidate.release_version, current.release_version)
  if (releaseCompare !== 0) return releaseCompare > 0

  return compareVersionRank(candidate.api_version, current.api_version) >= 0
}

function isFeatureRowActive(row, activeFeatureFlags) {
  if (!row?.feature_flag) return true

  return activeFeatureFlags.includes(row.feature_flag)
}

export function mergeMatrixRowsWithFeatureOverrides(
  releaseVersion,
  activeFeatureFlags = [],
  sourceRows = matrixRows,
) {
  const targetRelease = String(releaseVersion || getAppReleaseVersion()).trim()
  const flags = (activeFeatureFlags || []).filter(Boolean)
  const winners = new Map()

  ;(sourceRows || []).forEach((row) => {
    if (row?.archived || !rowApiName(row) || !rowApiVersionUrl(row)) return
    if (!isRowForRelease(row, targetRelease)) return
    if (!isFeatureRowActive(row, flags)) return

    const apiName = canonicalApiName(rowApiName(row))
    const current = winners.get(apiName)

    if (isNewerMatrixRow(row, current)) winners.set(apiName, row)
  })

  return [...winners.values()]
}

export function buildApiMappings(
  rows,
  releaseVersion = getAppReleaseVersion(),
  activeFeatureFlags = [],
) {
  const mappings = Object.create(null)
  const mergedRows = mergeMatrixRowsWithFeatureOverrides(releaseVersion, activeFeatureFlags, rows)

  mergedRows.forEach((row) => {
    const apiName = canonicalApiName(rowApiName(row))
    const apiVersionUrl = rowApiVersionUrl(row)

    if (apiName && apiVersionUrl) mappings[apiName] = apiVersionUrl
  })

  return mappings
}

export function setApiVersionMatrixRows(rows, options = {}) {
  matrixRows = Array.isArray(rows) ? rows.slice() : []
  const mappings = versionBasedApiUsageEnabled
    ? buildApiMappings(
        matrixRows,
        options.releaseVersion || getAppReleaseVersion(),
        options.activeFeatureFlags || [],
      )
    : Object.create(null)

  clearObject(apiVersionMatrix)
  Object.assign(apiVersionMatrix, mappings)

  return apiVersionMatrix
}

export function setVersionBasedApiUsageEnabled(isEnabled, options = {}) {
  versionBasedApiUsageEnabled = Boolean(isEnabled)
  return setApiVersionMatrixRows(matrixRows, options)
}

export function isVersionBasedApiUsageEnabled() {
  return versionBasedApiUsageEnabled
}

export function shouldUseVersionBasedApiNames(statsigClient) {
  return Boolean(statsigClient?.checkGate?.(VERSION_BASED_API_USAGE_FEATURE_FLAG))
}

export function syncVersionBasedApiUsageFromStatsig(statsigClient, options = {}) {
  return setVersionBasedApiUsageEnabled(shouldUseVersionBasedApiNames(statsigClient), options)
}

export function useApiVersionMatrixFeatureGate() {
  const { client: statsigClient } = useStatsigClient()
  const useVersionBasedApiNames = shouldUseVersionBasedApiNames(statsigClient)

  useEffect(() => {
    syncVersionBasedApiUsageFromStatsig(statsigClient)
  }, [statsigClient, useVersionBasedApiNames])

  return useVersionBasedApiNames
}

export function getApiVersionMatrixRows() {
  return matrixRows
}

export function resetApiVersionMatrixRuntimeState() {
  matrixRows = []
  hydrateAttempted = false
  hydrateSucceeded = false
  versionBasedApiUsageEnabled = false
  clearObject(apiVersionMatrix)
}

export function didApiVersionMatrixHydrateSucceed() {
  return hydrateSucceeded
}

export function hasApiVersionMatrixHydrationFinished() {
  return hydrateAttempted
}

export async function hydrateApiVersionMatrixFromClient(client, options = {}) {
  hydrateAttempted = true

  if (!client) {
    hydrateSucceeded = false
    setApiVersionMatrixRows([], options)
    return {
      data: [],
      error: new Error('hydrateApiVersionMatrixFromClient requires client'),
    }
  }

  const { data, error } = await client.from('api_version_matrix').select(`
      release_version,
      feature_flag,
      api_name,
      api_version_url,
      api_version,
      archived
    `)

  if (error) {
    console.error('API version matrix load error:', error)
    hydrateSucceeded = false
    setApiVersionMatrixRows([], options)
    return { data: [], error }
  }

  hydrateSucceeded = true
  setApiVersionMatrixRows(data || [], options)

  return { data: data || [], error: null }
}

export function getApiVersionUrl(
  apiName,
  releaseVersion = getAppReleaseVersion(),
  activeFeatureFlags = [],
) {
  if (!versionBasedApiUsageEnabled) return null

  const key = canonicalApiName(apiName)
  if (!key) return null

  if (releaseVersion === getAppReleaseVersion() && activeFeatureFlags.length === 0)
    return apiVersionMatrix[key] || null

  const mappings = buildApiMappings(matrixRows, releaseVersion, activeFeatureFlags)

  return mappings[key] || null
}

export function getApiVersion(apiName, _apiType, releaseVersion = getAppReleaseVersion()) {
  if (!versionBasedApiUsageEnabled) return null

  const key = canonicalApiName(apiName)
  const selectedRow = mergeMatrixRowsWithFeatureOverrides(releaseVersion, [], matrixRows).find(
    (row) => canonicalApiName(rowApiName(row)) === key,
  )

  return selectedRow?.api_version || null
}

export function resolveRpcFromApiVersions(apiName) {
  const resolvedApiUrl = getApiVersionUrl(apiName)
  if (!resolvedApiUrl) return apiName

  return postgresRpcBaseFromMatrixUrl(resolvedApiUrl) || apiName
}

export function resolveEdgeFromApiVersions(apiName) {
  const rawApiName = String(apiName || '')
  const queryIndex = rawApiName.indexOf('?')
  const hashIndex = rawApiName.indexOf('#')
  const suffixStartCandidates = [queryIndex, hashIndex].filter((index) => index !== -1)
  const suffixStart = suffixStartCandidates.length ? Math.min(...suffixStartCandidates) : -1
  const lookupName = suffixStart === -1 ? rawApiName : rawApiName.slice(0, suffixStart)
  const suffix = suffixStart === -1 ? '' : rawApiName.slice(suffixStart)
  const resolvedApiUrl = getApiVersionUrl(lookupName)

  return resolvedApiUrl ? `${resolvedApiUrl}${suffix}` : apiName
}

export const hydrateApiVersionMatrixFromSupabaseClient = hydrateApiVersionMatrixFromClient
